import { imagineStore } from "@/stores/imagine.store";
import { useEffect } from "react";
import { useStickToBottomContext } from "use-stick-to-bottom";
import { filter, withLatestFrom } from "rxjs/operators";

/**
 * ScrollToBottomCapturer
 *
 * Subscribes to scroll events from the chat store and triggers scroll-to-bottom
 * behavior when messages change, with support for scroll locking.
 *
 * How it works:
 * - Listens to `scrollToBottom$` Subject that emits whenever messages change
 * - Checks the current lock state from `scrollLocked$` BehaviorSubject
 * - Only scrolls if the scroll is NOT locked
 *
 * Scroll Locking:
 * - Use `lockScroll()` before loading older messages to prevent auto-scroll
 * - Use `unlockScroll()` after loading completes to restore normal behavior
 * - The lock prevents scroll during pagination, maintaining user's scroll position
 *
 * RxJS Pattern:
 * - `withLatestFrom` ensures we only emit when scrollToBottom$ fires (not when lock state changes)
 * - This prevents unwanted scrolling when unlocking; unlock itself won't trigger scroll
 * - `filter` blocks emissions when isLocked === true
 */
export function ScrollToBottomCapturer() {
  const { scrollToBottom } = useStickToBottomContext();
  const scrollToBottom$ = imagineStore.use.chat.scrollToBottom$();
  const scrollLocked$ = imagineStore.use.chat.scrollLocked$();

  useEffect(() => {
    if (scrollToBottom$ && scrollLocked$) {
      const subscription = scrollToBottom$
        .pipe(
          // Get latest lock state when scrollToBottom$ emits (doesn't emit on lock state change)
          withLatestFrom(scrollLocked$),
          // Only proceed if scroll is NOT locked
          filter(([_, isLocked]) => !isLocked),
        )
        .subscribe(() => {
          scrollToBottom();
        });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [scrollToBottom$, scrollLocked$, scrollToBottom]);

  return null;
}
